// Feature API - CRUD factory for dashboard feature resources
// Share fetchWithTimeout (retry + ApiError) với API.js để mọi feature CRUD đều có retry tự động
import { fetchWithTimeout } from './API';
import { handleUnauthorized } from './auth-events';

const CUNGVEDICH_API_BASE_URL = process.env.REACT_APP_CUNGVEDICH_API_URL || 'https://api.cungvedich.vn/api';

// Shared auth headers
const authHeaders = (accessToken) => ({
  'accept': '*/*',
  'x-access-token': accessToken,
  'Content-Type': 'application/json'
});

// Wrap fetch response into standard result shape
// API returns { statusCode, data: { count, rows } } — unwrap to keep data flat
const handleResponse = async (response) => {
  if (response.ok) {
    const json = await response.json().catch(() => ({}));
    // Unwrap nested envelope: use json.data if present, otherwise json itself
    const data = json.data !== undefined ? json.data : json;
    return { success: true, data };
  }
  // 401 = token hết hạn/blacklist → clear session + redirect /login (xem auth-events.js)
  // Trước đây: rò 'Unauthorized' thô từ BE ra UI làm đại lý hoang mang
  if (response.status === 401) {
    handleUnauthorized('token_invalid');
    return { success: false, error: 'Phiên đăng nhập đã hết hạn. Đang chuyển sang trang đăng nhập...' };
  }
  const errorData = await response.json().catch(() => ({}));
  return { success: false, error: errorData.message || `HTTP ${response.status}` };
};

// Factory: creates a CRUD API object for a given resource path
const createFeatureAPI = (resourcePath) => ({
  // GET list with optional filters (month/year + fromDate/toDate range)
  getList: async (accessToken, filters = {}) => {
    try {
      const { month, year, fromDate, toDate, pageSize = 999 } = filters;
      const params = new URLSearchParams();
      if (month != null && month !== '') params.append('month', month);
      if (year != null && year !== '') params.append('year', year);
      if (fromDate) params.append('fromDate', fromDate);
      if (toDate) params.append('toDate', toDate);
      params.append('pageSize', String(pageSize));
      const query = `?${params.toString()}`;
      const response = await fetchWithTimeout(
        `${CUNGVEDICH_API_BASE_URL}${resourcePath}${query}`,
        { method: 'GET', headers: authHeaders(accessToken) }
      );
      return handleResponse(response);
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // GET single record by id
  getById: async (accessToken, id) => {
    try {
      const response = await fetchWithTimeout(
        `${CUNGVEDICH_API_BASE_URL}${resourcePath}/${id}`,
        { method: 'GET', headers: authHeaders(accessToken) }
      );
      return handleResponse(response);
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // POST create new record — noRetry: idempotency (2 lần POST = tạo 2 record)
  create: async (accessToken, data) => {
    try {
      const response = await fetchWithTimeout(
        `${CUNGVEDICH_API_BASE_URL}${resourcePath}`,
        {
          method: 'POST',
          headers: authHeaders(accessToken),
          body: JSON.stringify(data),
          noRetry: true,
        }
      );
      return handleResponse(response);
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // PUT update existing record — noRetry để tránh duplicate write race
  update: async (accessToken, id, data) => {
    try {
      const response = await fetchWithTimeout(
        `${CUNGVEDICH_API_BASE_URL}${resourcePath}/${id}`,
        {
          method: 'PUT',
          headers: authHeaders(accessToken),
          body: JSON.stringify(data),
          noRetry: true,
        }
      );
      return handleResponse(response);
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // DELETE record by id — noRetry, server có thể đã xoá rồi
  delete: async (accessToken, id) => {
    try {
      const response = await fetchWithTimeout(
        `${CUNGVEDICH_API_BASE_URL}${resourcePath}/${id}`,
        { method: 'DELETE', headers: authHeaders(accessToken), noRetry: true }
      );
      return handleResponse(response);
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
});

// Named feature API instances
export const ReelsAPI = createFeatureAPI('/reels');
export const ReelTrendingAPI = createFeatureAPI('/reel-trending');
export const LivestreamAPI = createFeatureAPI('/livestreams');
export const MarketAPI = createFeatureAPI('/markets');
export const SpaMarketAPI = createFeatureAPI('/spa-markets');
export const DailyReportAPI = createFeatureAPI('/daily-reports');
export const ToggleSalesAPI = createFeatureAPI('/toggle-sales');
export const TeamMeetingAPI = createFeatureAPI('/team-meetings');

// Sale Report Submissions — approval workflow for monthly sales reports
export const SaleReportSubmissionAPI = {
  getMine: async (accessToken, month, year) => {
    try {
      const params = new URLSearchParams({ month, year });
      const response = await fetchWithTimeout(
        `${CUNGVEDICH_API_BASE_URL}/sale-report-submissions/mine?${params}`,
        { method: 'GET', headers: authHeaders(accessToken) }
      );
      return handleResponse(response);
    } catch (error) {
      return { success: false, error: error.message };
    }
  },
  // Doanh số + trạng thái duyệt 12 tháng của chính mình → lịch trên /doanh-so. Trả { data: { year, months: [...] } }
  getYearly: async (accessToken, year) => {
    try {
      const params = new URLSearchParams({ year });
      const response = await fetchWithTimeout(
        `${CUNGVEDICH_API_BASE_URL}/sale-report-submissions/yearly?${params}`,
        { method: 'GET', headers: authHeaders(accessToken) }
      );
      return handleResponse(response);
    } catch (error) {
      return { success: false, error: error.message };
    }
  },
  submit: async (accessToken, payload) => {
    try {
      const response = await fetchWithTimeout(
        `${CUNGVEDICH_API_BASE_URL}/sale-report-submissions`,
        { method: 'POST', headers: authHeaders(accessToken), body: JSON.stringify(payload), noRetry: true }
      );
      return handleResponse(response);
    } catch (error) {
      return { success: false, error: error.message };
    }
  },
};

// Dashboard stats — single aggregated endpoint replaces multiple feature calls
export const DashboardAPI = {
  getStats: async (accessToken, month, year) => {
    try {
      const params = new URLSearchParams();
      if (month != null) params.append('month', month);
      if (year != null) params.append('year', year);
      const response = await fetchWithTimeout(
        `${CUNGVEDICH_API_BASE_URL}/dashboard/stats?${params}`,
        { method: 'GET', headers: authHeaders(accessToken) }
      );
      return handleResponse(response);
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
};

// Scores — server-side score calculation
export const ScoresAPI = {
  getScores: async (accessToken, month, year) => {
    try {
      const params = new URLSearchParams();
      if (month != null) params.append('month', month);
      if (year != null) params.append('year', year);
      const response = await fetchWithTimeout(
        `${CUNGVEDICH_API_BASE_URL}/scores?${params}`,
        { method: 'GET', headers: authHeaders(accessToken) }
      );
      return handleResponse(response);
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
};

// F1 Management — aggregated member list with search + pagination
export const F1API = {
  getList: async (accessToken, { page = 1, pageSize = 30, search = '', month, year, fromDate, toDate, startDate, endDate } = {}) => {
    try {
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('pageSize', String(pageSize));
      if (search) params.append('search', search);
      if (month) params.append('month', String(month));
      if (year) params.append('year', String(year));
      if (fromDate) params.append('fromDate', fromDate);
      if (toDate) params.append('toDate', toDate);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const response = await fetchWithTimeout(
        `${CUNGVEDICH_API_BASE_URL}/f1-management?${params}`,
        { method: 'GET', headers: authHeaders(accessToken) }
      );
      const result = await handleResponse(response);
      if (result.success) {
        return {
          success: true,
          rows: result.data?.rows || [],
          count: result.data?.count || 0,
          summary: result.data?.summary || {},
          // totals = hàng TỔNG của bảng (cộng toàn bộ F1, không phải chỉ trang đang xem)
          totals: result.data?.totals || null,
          targetMonth: result.data?.targetMonth,
          targetYear: result.data?.targetYear,
        };
      }
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  },
  getSaleDetails: async (accessToken, userId, month, year) => {
    try {
      const params = new URLSearchParams({ userId: String(userId), month: String(month), year: String(year) });
      const response = await fetchWithTimeout(
        `${CUNGVEDICH_API_BASE_URL}/f1-management/sale-details?${params}`,
        { method: 'GET', headers: authHeaders(accessToken) }
      );
      return handleResponse(response);
    } catch (error) {
      return { success: false, error: error.message };
    }
  },
  // sale-details = doanh số NHẬP (sale_reports); sell-details = doanh số BÁN (order_details). Đừng nhầm.
  getSellDetails: async (accessToken, userId, month, year) => {
    try {
      const params = new URLSearchParams({ userId: String(userId), month: String(month), year: String(year) });
      const response = await fetchWithTimeout(
        `${CUNGVEDICH_API_BASE_URL}/f1-management/sell-details?${params}`,
        { method: 'GET', headers: authHeaders(accessToken) }
      );
      return handleResponse(response);
    } catch (error) {
      return { success: false, error: error.message };
    }
  },
  // QT Quân Số (trang /quan-so): tổng quân số + người mới toàn nhánh. date = lọc đúng 1 ngày khi bấm cột biểu đồ.
  getQuanSo: async (accessToken, { days = 10, date = null } = {}) => {
    try {
      const params = new URLSearchParams({ days: String(days) });
      if (date) params.append('date', date);
      const response = await fetchWithTimeout(
        `${CUNGVEDICH_API_BASE_URL}/f1-management/quan-so?${params}`,
        { method: 'GET', headers: authHeaders(accessToken) }
      );
      return handleResponse(response);
    } catch (error) {
      return { success: false, error: error.message };
    }
  },
  // Token 15' để impersonate (xem /f1 dưới tài khoản 1 F1 trực tiếp). Trả { data: { token } } hoặc { data: { denied } }.
  getImpersonateToken: async (accessToken, userId) => {
    try {
      const response = await fetchWithTimeout(
        `${CUNGVEDICH_API_BASE_URL}/f1-management/${userId}/impersonate-token`,
        { method: 'GET', headers: authHeaders(accessToken) }
      );
      return handleResponse(response);
    } catch (error) {
      return { success: false, error: error.message };
    }
  },
};

// Reels Auto Check — Facebook Graph API (no profile setup needed)
// Thông điệp ngắn (100 câu triết lý)
export const ThongDiepAPI = {
  ...createFeatureAPI('/thong-diep'),
  getRandom: async (accessToken) => {
    try {
      const response = await fetchWithTimeout(
        `${CUNGVEDICH_API_BASE_URL}/thong-diep/random`,
        { method: 'GET', headers: authHeaders(accessToken) }
      );
      return handleResponse(response);
    } catch (error) {
      return { success: false, error: error.message };
    }
  },
  getGrouped: async (accessToken) => {
    try {
      const response = await fetchWithTimeout(
        `${CUNGVEDICH_API_BASE_URL}/thong-diep/grouped`,
        { method: 'GET', headers: authHeaders(accessToken) }
      );
      return handleResponse(response);
    } catch (error) {
      return { success: false, error: error.message };
    }
  },
};

// Khen thưởng (STT 20) — feed /khenthuong. BE: PE_Core modules/khen-thuong (mount /khen-thuong).
// dangKy noRetry: 2 lần POST = lỗi "đã đăng ký" lần 2, không tạo trùng nhưng vẫn tránh gửi lặp.
const ktGet = async (accessToken, path) => {
  try {
    const response = await fetchWithTimeout(
      `${CUNGVEDICH_API_BASE_URL}/khen-thuong${path}`,
      { method: 'GET', headers: authHeaders(accessToken) }
    );
    return handleResponse(response);
  } catch (error) {
    return { success: false, error: error.message };
  }
};
export const KhenThuongAPI = {
  getList: (accessToken) => ktGet(accessToken, ''),
  getByMa: (accessToken, ma) => ktGet(accessToken, `/${encodeURIComponent(ma)}`),
  getDieuKien: (accessToken) => ktGet(accessToken, '/du-lich-2026/dieu-kien'),
  getCuaToi: (accessToken) => ktGet(accessToken, '/du-lich-2026/cua-toi'),
  dangKy: async (accessToken, data) => {
    try {
      const response = await fetchWithTimeout(
        `${CUNGVEDICH_API_BASE_URL}/khen-thuong/du-lich-2026/dang-ky`,
        { method: 'POST', headers: authHeaders(accessToken), body: JSON.stringify(data), noRetry: true }
      );
      return handleResponse(response);
    } catch (error) {
      return { success: false, error: error.message };
    }
  },
};

// Tổng doanh số theo tháng (dùng cho Khoe thành tích tháng).
// Response: { soLe: {count,totalQty,totalAmount}, soSi: {...}, activeDays }.
// soLe = đơn lẻ (orders.type=1), soSi = đơn sỉ (orders.type=2). Đừng tự hoán đổi.
export const MonthlySalesAPI = {
  getSummary: async (accessToken, month, year) => {
    try {
      const params = new URLSearchParams({ month, year });
      const response = await fetchWithTimeout(
        `${CUNGVEDICH_API_BASE_URL}/orders/monthly-summary?${params}`,
        { method: 'GET', headers: authHeaders(accessToken) }
      );
      return handleResponse(response);
    } catch (error) {
      return { success: false, error: error.message };
    }
  },
};

// Tổng doanh số cả NĂM, chia theo tháng + lẻ/sỉ. Dùng cho danh hiệu (Khoe thành tích tháng).
// Response: { year, totalYear, totalCountLe, totalCountSi, months: [{month, leAmount, siAmount, leCount, siCount, total}] }.
export const YearlySalesAPI = {
  getSummary: async (accessToken, year) => {
    try {
      const params = new URLSearchParams({ year });
      const response = await fetchWithTimeout(
        `${CUNGVEDICH_API_BASE_URL}/orders/yearly-summary?${params}`,
        { method: 'GET', headers: authHeaders(accessToken) }
      );
      return handleResponse(response);
    } catch (error) {
      return { success: false, error: error.message };
    }
  },
};

// Thang mốc danh hiệu (tham chiếu doanh thu lũy kế năm → tên chức danh).
// Response: [{ id, stt, tenDanhHieu, doanhThuMin, doanhThuMax(null=đỉnh), moTa, mauSac, icon }].
export const MocDanhHieuAPI = {
  getLadder: async (accessToken) => {
    try {
      const response = await fetchWithTimeout(
        `${CUNGVEDICH_API_BASE_URL}/moc-danh-hieu`,
        { method: 'GET', headers: authHeaders(accessToken) }
      );
      return handleResponse(response);
    } catch (error) {
      return { success: false, error: error.message };
    }
  },
};

// Tổng doanh số theo ngày (dùng cho Khoe thành tích ngày).
// soLe = đơn lẻ (orders.type=1), soSi = đơn sỉ (orders.type=2). Đừng tự hoán đổi.
export const DailySalesAPI = {
  getSummary: async (accessToken, date) => {
    try {
      const params = new URLSearchParams();
      if (date) params.append('date', date);
      const response = await fetchWithTimeout(
        `${CUNGVEDICH_API_BASE_URL}/orders/daily-summary?${params}`,
        { method: 'GET', headers: authHeaders(accessToken) }
      );
      return handleResponse(response);
    } catch (error) {
      return { success: false, error: error.message };
    }
  },
};

export const ReelsAutoCheckAPI = {
  getProfile: async (accessToken) => {
    try {
      const response = await fetchWithTimeout(
        `${CUNGVEDICH_API_BASE_URL}/reels-auto-check/profile`,
        { method: 'GET', headers: authHeaders(accessToken) }
      );
      return handleResponse(response);
    } catch (error) {
      return { success: false, error: error.message };
    }
  },
  submitReel: async (accessToken, reelUrl) => {
    try {
      const response = await fetchWithTimeout(
        `${CUNGVEDICH_API_BASE_URL}/reels-auto-check`,
        { method: 'POST', headers: authHeaders(accessToken), body: JSON.stringify({ reelUrl }), timeout: 30000, noRetry: true }
      );
      return handleResponse(response);
    } catch (error) {
      return { success: false, error: error.message };
    }
  },
  getList: async (accessToken, filters = {}) => {
    try {
      const { month, year, fromDate, toDate, pageSize = 999 } = filters;
      const params = new URLSearchParams();
      if (month != null && month !== '') params.append('month', month);
      if (year != null && year !== '') params.append('year', year);
      if (fromDate) params.append('fromDate', fromDate);
      if (toDate) params.append('toDate', toDate);
      params.append('pageSize', String(pageSize));
      const response = await fetchWithTimeout(
        `${CUNGVEDICH_API_BASE_URL}/reels-auto-check?${params}`,
        { method: 'GET', headers: authHeaders(accessToken) }
      );
      return handleResponse(response);
    } catch (error) {
      return { success: false, error: error.message };
    }
  },
  delete: async (accessToken, id) => {
    try {
      const response = await fetchWithTimeout(
        `${CUNGVEDICH_API_BASE_URL}/reels-auto-check/${id}`,
        { method: 'DELETE', headers: authHeaders(accessToken), noRetry: true }
      );
      return handleResponse(response);
    } catch (error) {
      return { success: false, error: error.message };
    }
  },
};

// Shared image upload utility — timeout 60s (upload), noRetry (mutate)
export const uploadImage = async (accessToken, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetchWithTimeout(
      `${CUNGVEDICH_API_BASE_URL}/system/file`,
      {
        method: 'POST',
        headers: {
          'accept': '*/*',
          'x-access-token': accessToken
          // Content-Type omitted — browser sets multipart boundary automatically
        },
        body: formData,
        timeout: 60000,
        noRetry: true,
      }
    );
    if (response.ok) {
      const data = await response.json().catch(() => ({}));
      // API may return { fileUrl } directly or wrap in { data: { fileUrl } }
      const fileData = data.data || data;
      const url = fileData.fileUrl || fileData.imageUrl || fileData.url;
      if (!url) return { success: false, error: 'Không tìm thấy URL ảnh trong phản hồi' };
      return { success: true, imageUrl: url };
    }
    const errorData = await response.json().catch(() => ({}));
    return { success: false, error: errorData.message || `HTTP ${response.status}` };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// QL Khách lẻ (STT 8) — feed /khach-le. BE: PE_Core modules/khach-le (mount /khach-le, đọc view khach_le_yody).
const klFetch = async (accessToken, path, init = {}) => {
  try {
    const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/khach-le${path}`, {
      headers: authHeaders(accessToken), ...init,
    });
    return handleResponse(response);
  } catch (error) {
    return { success: false, error: error.message };
  }
};
// 23.1 Feedback cá nhân + 24 Kho feedback công ty — PE_Core /feedback (bảng khach_le_feedback, dùng chung ⑥ QL Khách lẻ)
const fbFetch = async (accessToken, path, init = {}) => {
  try {
    const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/feedback${path}`, {
      headers: authHeaders(accessToken), ...init,
    });
    return handleResponse(response);
  } catch (error) {
    return { success: false, error: error.message };
  }
};
export const FeedbackAPI = {
  danhMuc: (accessToken) => fbFetch(accessToken, '/danh-muc'),
  timNguoi: (accessToken, q) => fbFetch(accessToken, `/nguoi?q=${encodeURIComponent(q || '')}`),
  cuaToi: (accessToken) => fbFetch(accessToken, ''),
  congTy: (accessToken) => fbFetch(accessToken, '/cong-ty'),
  chiTiet: (accessToken, id) => fbFetch(accessToken, `/${id}`),
  tao: (accessToken, data) => fbFetch(accessToken, '', { method: 'POST', body: JSON.stringify(data), noRetry: true }),
  sua: (accessToken, id, data) => fbFetch(accessToken, `/${id}`, { method: 'PUT', body: JSON.stringify(data), noRetry: true }),
  xoa: (accessToken, id) => fbFetch(accessToken, `/${id}`, { method: 'DELETE', noRetry: true }),
};

// 17 Kiến thức sản phẩm — ô Đang triển khai có mật khẩu: mọi lệnh gửi kèm header x-mat-khau (sai → 403)
const ltFetch = async (accessToken, mk, path) => {
  try {
    const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/lieu-trinh${path}`, { headers: { ...authHeaders(accessToken), 'x-mat-khau': mk || '' } });
    return handleResponse(response);
  } catch (error) {
    return { success: false, error: error.message };
  }
};
export const LieuTrinhAPI = {
  moKhoa: (accessToken, mk) => ltFetch(accessToken, mk, '/mo-khoa'),
  list: (accessToken, mk) => ltFetch(accessToken, mk, ''),
  sanPham: (accessToken, mk) => ltFetch(accessToken, mk, '/san-pham'),
};

// 16 Kiến thức kinh doanh → Combo sản phẩm — PE_Core /combo (bảng combo_san_pham, admin /combo-san-pham)
const cbFetch = (accessToken, path) => fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/combo${path}`, { headers: authHeaders(accessToken) })
  .then(handleResponse).catch((e) => ({ success: false, error: e.message }));
export const ComboAPI = {
  list: (accessToken) => cbFetch(accessToken, ''),
  chiTiet: (accessToken, id) => cbFetch(accessToken, `/${id}`),
};

// Video đào tạo Mux — PE_Core /video (bảng video_dao_tao, video_xem). keepalive=true khi rời trang (fetch vẫn đi dù tab đóng).
export const VideoAPI = {
  xem: (accessToken, ma) => fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/video/${encodeURIComponent(ma)}`, { headers: authHeaders(accessToken) })
    .then(handleResponse).catch((e) => ({ success: false, error: e.message })),
  tienDo: (accessToken, ma, body, keepalive = false) => fetch(`${CUNGVEDICH_API_BASE_URL}/video/${encodeURIComponent(ma)}/tien-do`, {
    method: 'POST', headers: authHeaders(accessToken), body: JSON.stringify(body), keepalive,
  }).then(handleResponse).catch((e) => ({ success: false, error: e.message })),
};

export const KhachLeAPI = {
  getList: (accessToken) => klFetch(accessToken, ''),
  capNhat: (accessToken, khachId, data) => klFetch(accessToken, `/${khachId}`, { method: 'PUT', body: JSON.stringify(data), noRetry: true }), // { tinhTrang?, ghiChu?, tinh?, diaChi? }
  suaThongTin: (accessToken, khachId, data) => klFetch(accessToken, `/${khachId}/sdt`, { method: 'PUT', body: JSON.stringify(data), noRetry: true }), // { ten?, sdt?, gop? }
  danhMuc: (accessToken) => klFetch(accessToken, '/danh-muc'),
  getDon: (accessToken, khachId) => klFetch(accessToken, `/${khachId}/don`),
  getViec: (accessToken, khachId) => klFetch(accessToken, `/${khachId}/viec`),
  themViec: (accessToken, khachId, data) => klFetch(accessToken, `/${khachId}/viec`, { method: 'POST', body: JSON.stringify(data), noRetry: true }),
  suaViec: (accessToken, id, data) => klFetch(accessToken, `/viec/${id}`, { method: 'PUT', body: JSON.stringify(data), noRetry: true }),
  xoaViec: (accessToken, id) => klFetch(accessToken, `/viec/${id}`, { method: 'DELETE', noRetry: true }),
  viecHomNay: (accessToken) => klFetch(accessToken, '/viec-hom-nay'),
  getZalo: (accessToken) => klFetch(accessToken, '/zalo'),
  ketNoiZalo: (accessToken) => klFetch(accessToken, '/zalo/ket-noi', { method: 'POST', noRetry: true }),
};
